'use client';

import { useEffect, useRef, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import {
  isAuthenticated,
  getUserRole,
  getDefaultRedirect,
  ROLES,
  ROUTE_PERMISSIONS,
  type Role,
} from '@/lib/auth';

/**
 * Component that monitors role changes and automatically redirects users
 * to appropriate pages when their role changes in the backend.
 * 
 * This component:
 * - Monitors role changes every 2 seconds (via useSyncCurrentUserRole hook)
 * - Detects when role changes by comparing current role with previous role
 * - Checks if current route is accessible with new role
 * - Redirects to default page for new role if current route is not accessible
 * - Refreshes page content when role changes (even if route is still accessible)
 * - Handles edge cases (public routes, network failures, invalid roles, concurrent navigation)
 */
export default function RoleChangeRedirect() {
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const previousRoleRef = useRef<Role | null>(null);
  const isRedirectingRef = useRef(false);
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  // Public routes that should not trigger redirects
  const publicRoutes = ['/login', '/register', '/verify-otp', '/service', '/'];

  const isPublicRoute = (route: string | null): boolean => {
    if (!route) return false;
    return publicRoutes.some(
      (publicRoute) => route === publicRoute || route.startsWith(publicRoute + '/')
    );
  };

  // Helper function to check if a route is accessible with a specific role
  // This function uses the role parameter directly instead of reading from localStorage
  // to avoid race conditions where localStorage hasn't been updated yet
  const checkRouteAccessWithRole = useCallback((role: Role | null, route: string | null): boolean => {
    if (!route || !role) return false;
    
    // Public routes are always accessible
    if (isPublicRoute(route)) return true;
    
    // Check if user is authenticated
    if (!isAuthenticated()) {
      return false;
    }
    
    // Find matching route permission
    let matchedRoute: string | null = null;
    let matchedRoles: Role[] = [];
    
    // Check exact match first
    if (ROUTE_PERMISSIONS[route]) {
      matchedRoute = route;
      matchedRoles = ROUTE_PERMISSIONS[route];
    } else {
      // Check for partial matches (e.g., /home/settings matches /home)
      const sortedRoutes = Object.keys(ROUTE_PERMISSIONS).sort((a, b) => b.length - a.length);
      for (const r of sortedRoutes) {
        if (route.startsWith(r)) {
          matchedRoute = r;
          matchedRoles = ROUTE_PERMISSIONS[r];
          break;
        }
      }
    }
    
    // If no route permission defined, default to requiring authentication only
    if (!matchedRoute) {
      return isAuthenticated();
    }
    
    // Check if role has permission
    return matchedRoles.includes(role);
  }, []);

  // Function to refresh page content when role changes (even if route is still accessible)
  const refreshPageContent = useCallback((previousRole: Role | null, newRole: Role | null) => {
    console.log(`Role changed from ${previousRole} to ${newRole}. Refreshing page content...`);
    
    // Invalidate all react-query caches to force refetch with new role permissions
    queryClient.invalidateQueries();
    
    // Dispatch a custom event so page components can react to role changes
    window.dispatchEvent(new CustomEvent('roleChangedRefresh', { 
      detail: { previousRole, newRole } 
    }));
    
    // Force router refresh to re-render page with new role context
    router.refresh();
  }, [queryClient, router]);

  // Check if current route is accessible with the given role
  const checkAndRedirect = useCallback((newRole: Role | null, currentPathname: string | null, forceCheck: boolean = false) => {
    // Don't redirect if already redirecting (unless forced)
    if (isRedirectingRef.current && !forceCheck) {
      // If we've been redirecting for more than 2 seconds, reset the flag
      setTimeout(() => {
        if (isRedirectingRef.current) {
          console.warn('Redirect flag stuck, resetting...');
          isRedirectingRef.current = false;
        }
      }, 2000);
      return;
    }

    // Don't redirect on public routes (FR-010)
    if (isPublicRoute(currentPathname)) {
      return;
    }

    // Don't redirect if user is not authenticated
    if (!isAuthenticated()) {
      return;
    }

    // Handle invalid or unrecognized role (FR-008)
    if (!newRole || !Object.values(ROLES).includes(newRole)) {
      console.warn('Invalid role detected, redirecting to login');
      isRedirectingRef.current = true;
      router.replace('/login');
      setTimeout(() => {
        isRedirectingRef.current = false;
      }, 1000);
      return;
    }

    // Check if current route is accessible with new role using the specific role check
    const canAccess = checkRouteAccessWithRole(newRole, currentPathname);

    // If route is accessible with new role, no redirect needed (FR-006)
    // But we still need to refresh page content to reflect new role permissions
    if (canAccess) {
      // Reset redirect flag if route is accessible
      if (isRedirectingRef.current) {
        isRedirectingRef.current = false;
      }
      // Refresh page content if this is a role change (not initial load)
      if (previousRoleRef.current !== null && previousRoleRef.current !== newRole) {
        refreshPageContent(previousRoleRef.current, newRole);
      }
      return;
    }

    // Route is not accessible with new role, redirect to default page (FR-002, FR-003)
    const defaultRedirect = getDefaultRedirect(newRole);
    
    // Prevent redirect conflicts (FR-009)
    if (currentPathname === defaultRedirect) {
      return;
    }

    console.log(
      `Role changed from ${previousRoleRef.current} to ${newRole}. ` +
      `Current route ${currentPathname} is not accessible. Redirecting to ${defaultRedirect}`
    );

    isRedirectingRef.current = true;
    
    // Use replace instead of push to avoid adding to history (seamless redirect)
    router.replace(defaultRedirect);
    
    // Reset redirecting flag after a short delay, with a fallback timeout
    const resetTimeout = setTimeout(() => {
      isRedirectingRef.current = false;
      retryCountRef.current = 0;
    }, 1000);
    
    // Fallback: force reset after 3 seconds if redirect seems stuck
    setTimeout(() => {
      if (isRedirectingRef.current) {
        console.warn('Redirect flag not reset after 3 seconds, forcing reset');
        isRedirectingRef.current = false;
        clearTimeout(resetTimeout);
      }
    }, 3000);
  }, [router, checkRouteAccessWithRole, refreshPageContent]);

  // Monitor role changes
  useEffect(() => {
    if (!isAuthenticated()) {
      previousRoleRef.current = null;
      return;
    }

    // Initialize previous role and check initial route accessibility
    if (previousRoleRef.current === null) {
      const initialRole = getUserRole();
      previousRoleRef.current = initialRole;
      
      // Check if current route is accessible with initial role
      // This prevents users from being stuck on inaccessible pages
      if (initialRole && pathname && !isPublicRoute(pathname)) {
        const canAccess = checkRouteAccessWithRole(initialRole, pathname);
        if (!canAccess) {
          console.log(`Initial route check: ${pathname} is not accessible with role ${initialRole}, redirecting...`);
          checkAndRedirect(initialRole, pathname, true);
        }
      }
      return;
    }

    // Check for role changes periodically
    const checkRoleChange = () => {
      try {
        const currentRole = getUserRole();

        // If role changed, handle redirect
        if (currentRole !== previousRoleRef.current) {
          console.log(
            `Role change detected: ${previousRoleRef.current} -> ${currentRole}`
          );
          
          // Reset retry count on successful role change detection
          retryCountRef.current = 0;
          
          // Small delay to ensure localStorage is fully updated
          setTimeout(() => {
            checkAndRedirect(currentRole, pathname, false);
            previousRoleRef.current = currentRole;
          }, 50);
        } else {
          // Even if role hasn't changed, verify route accessibility
          // This handles cases where user navigated to an inaccessible page
          if (currentRole && pathname && !isPublicRoute(pathname)) {
            const canAccess = checkRouteAccessWithRole(currentRole, pathname);
            if (!canAccess && !isRedirectingRef.current) {
              console.log(`Route accessibility check: ${pathname} is not accessible with role ${currentRole}, redirecting...`);
              checkAndRedirect(currentRole, pathname, true);
            }
          }
        }
      } catch (error) {
        console.error('Error checking role change:', error);
        
        // Retry logic for network failures (FR-007)
        retryCountRef.current += 1;
        if (retryCountRef.current < maxRetries) {
          console.log(`Retrying role check (attempt ${retryCountRef.current + 1}/${maxRetries})`);
        } else {
          console.error('Max retries reached for role check');
          retryCountRef.current = 0;
        }
      }
    };

    // Check immediately
    checkRoleChange();

    // Check every 2 seconds (FR-001: detect within 2 seconds)
    const interval = setInterval(checkRoleChange, 2000);

    return () => clearInterval(interval);
  }, [pathname, checkAndRedirect, checkRouteAccessWithRole]);

  // Listen for role change events (from useSyncCurrentUserRole hook)
  useEffect(() => {
    if (!isAuthenticated()) return;

    const handleRoleChangeEvent = () => {
      // Small delay to ensure localStorage is updated
      setTimeout(() => {
        const currentRole = getUserRole();
        const currentPathname = pathname;
        if (currentRole !== previousRoleRef.current) {
          console.log(`Role change event received: ${previousRoleRef.current} -> ${currentRole}`);
          checkAndRedirect(currentRole, currentPathname, false);
          previousRoleRef.current = currentRole;
        } else {
          // Even if role hasn't changed, verify route accessibility
          if (currentRole && currentPathname && !isPublicRoute(currentPathname)) {
            const canAccess = checkRouteAccessWithRole(currentRole, currentPathname);
            if (!canAccess && !isRedirectingRef.current) {
              console.log(`Route accessibility check after event: ${currentPathname} is not accessible, redirecting...`);
              checkAndRedirect(currentRole, currentPathname, true);
            }
          }
        }
      }, 150);
    };

    window.addEventListener('userRoleChanged', handleRoleChangeEvent);

    return () => {
      window.removeEventListener('userRoleChanged', handleRoleChangeEvent);
    };
  }, [pathname, checkAndRedirect, checkRouteAccessWithRole]);

  // Listen for storage changes (for multi-tab scenarios)
  useEffect(() => {
    if (!isAuthenticated()) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userRole' && e.newValue) {
        const newRole = e.newValue as Role;
        const currentPathname = pathname;
        if (newRole !== previousRoleRef.current) {
          console.log(`Storage change detected: ${previousRoleRef.current} -> ${newRole}`);
          setTimeout(() => {
            checkAndRedirect(newRole, currentPathname, false);
            previousRoleRef.current = newRole;
          }, 100);
        } else {
          // Verify route accessibility even if role hasn't changed
          if (newRole && currentPathname && !isPublicRoute(currentPathname)) {
            const canAccess = checkRouteAccessWithRole(newRole, currentPathname);
            if (!canAccess && !isRedirectingRef.current) {
              console.log(`Route accessibility check after storage change: ${currentPathname} is not accessible, redirecting...`);
              checkAndRedirect(newRole, currentPathname, true);
            }
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [pathname, checkAndRedirect, checkRouteAccessWithRole]);

  // This component doesn't render anything
  return null;
}

